import type { Identifier, RaRecord } from "ra-core";
import type { ComponentType } from "react";

import type { LeadAssignmentReason } from "./carouselTypes";
import type {
  COMPANY_CREATED,
  CONTACT_CREATED,
  CONTACT_NOTE_CREATED,
  DEAL_CREATED,
  DEAL_NOTE_CREATED,
} from "./consts";

export type SignUpData = {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
};

export const APP_ROLES = [
  "direccion",
  "lead_manager",
  "lider",
  "asesora",
  "administracion",
  "compliance",
  "contabilidad",
  "auditoria",
] as const;

export type AppRole = (typeof APP_ROLES)[number];

/**
 * Almikia org model (Almikia Epic E3b, TASK-057/061). Column-name contract
 * shared verbatim with the `public.sales` SQL migration (TASK-057) --
 * `business_arm` is nullable with no default/backfill. Spanish domain values
 * per the project's english-only rule (English identifiers, Spanish runtime
 * data).
 */
export const BUSINESS_ARMS = [
  "corretaje",
  "bpo",
  "ambos",
  "direccion_general",
] as const;
export type BusinessArm = (typeof BUSINESS_ARMS)[number];

/**
 * UI label "Unidad de negocio" (EC2c). 'ambos' is legacy: it is still stored
 * and read for old rows but can no longer be picked as a new value.
 */
export const LEGACY_BUSINESS_ARM: BusinessArm = "ambos";

/** True for a legacy person whose stored unit must be reviewed by Direccion. */
export const isLegacyBusinessArm = (
  arm: BusinessArm | null | undefined,
): boolean => arm === LEGACY_BUSINESS_ARM;

export type SalesFormData = {
  avatar?: string;
  email: string;
  secondary_emails?: string[];
  password?: string;
  first_name: string;
  last_name: string;
  role: AppRole;
  disabled: boolean;
  /** UI label "Unidad de negocio". */
  business_arm?: BusinessArm | null;
  /**
   * The user this one is accompanied/coordinated by (Independence-guide
   * wording -- never "reports to" / "manager"). References another `sales`
   * row.
   */
  reports_to?: Identifier | null;
  /** UI label "Clave de asesora". Unique per advisor. */
  advisor_code?: string | null;
  /**
   * UI label "Ventas acumuladas a la fecha". Written only by direccion,
   * through the `set_accumulated_sales_at_onboarding` RPC (never the users
   * function).
   */
  accumulated_sales_at_onboarding?: number;
};

export type Sale = {
  first_name: string;
  last_name: string;
  /**
   * Kept in sync by the backend: administrator = (role = 'direccion')
   */
  administrator: boolean;
  role: AppRole;
  avatar?: RAFile;
  disabled?: boolean;
  user_id: string;
  business_arm?: BusinessArm | null;
  reports_to?: Identifier | null;
  advisor_code?: string | null;
  /** Sales accumulated before the CRM, captured once at onboarding (0 by default). */
  accumulated_sales_at_onboarding?: number;
  /**
   * Almikia Epic EL3: per-person permission to reassign proactivo / colega leads (NOT a role).
   * Writable only by direccion and never on herself; absent / false = no permission.
   */
  can_reassign_protected_leads?: boolean;
  /**
   * Almikia Epic EW3: per-person permission to classify WhatsApp contacts (the 'Sin clasificar'
   * tray and the personal-numbers list). Writable only by direccion, never on herself.
   */
  can_classify_whatsapp_contacts?: boolean;

  /**
   * This is a copy of the user's email, to make it easier to handle by react admin
   * DO NOT UPDATE this field directly, it should be updated by the backend
   */
  email: string;

  secondary_emails?: string[];

  /**
   * This is used by the fake rest provider to store the password
   * DO NOT USE this field in your code besides the fake rest provider
   * @deprecated
   */
  password?: string;
} & Pick<RaRecord, "id">;

export type Company = {
  name: string;
  logo: RAFile;
  sector: string;
  size: 1 | 10 | 50 | 250 | 500;
  linkedin_url: string;
  website: string;
  phone_number: string;
  address: string;
  zipcode: string;
  city: string;
  state_abbr: string;
  sales_id?: Identifier;
  created_at: string;
  description: string;
  revenue: string;
  tax_identifier: string;
  country: string;
  context_links?: string[];
  nb_contacts?: number;
  nb_deals?: number;
} & Pick<RaRecord, "id">;

export type EmailAndType = {
  email: string;
  type: "Work" | "Home" | "Other";
};

export type PhoneNumberAndType = {
  number: string;
  type: "Work" | "Home" | "Other";
};

/**
 * Almikia Epic EC2a: the roles of a contact (`contacts.contact_types`, one address book).
 * English identifiers; must match `contacts_contact_types_check` in `01_tables.sql`.
 */
export const CONTACT_TYPES = [
  "lead_buyer",
  "lead_tenant",
  "client",
  "tenant",
  "guarantor",
  "owner_seller",
  "owner_lessor",
  "colega",
  "supplier",
  "ally",
  "developer",
] as const;
export type ContactType = (typeof CONTACT_TYPES)[number];

/** Almikia Epic EC2a: `contacts.business_unit` (Unidad de negocio). */
export const BUSINESS_UNITS = ["corretaje", "bpo"] as const;
export type BusinessUnit = (typeof BUSINESS_UNITS)[number];

/** Almikia Epic EC2a: `contacts.colega_kind` and `contacts.colega_affiliation`. */
export type ColegaKind = "independiente" | "inmobiliaria";
export type ColegaAffiliation = "ampi" | "otra" | "ninguna";

export type Contact = {
  /** Nombre(s). */
  first_name: string;
  /** Apellido paterno (a surname whose split is uncertain stays whole here). */
  last_name: string;
  title: string;
  company_id?: Identifier | null;
  email_jsonb: EmailAndType[];
  avatar?: Partial<RAFile>;
  /** Legacy: kept in the database, no longer written by the app. */
  linkedin_url?: string | null;
  first_seen: string;
  last_seen: string;
  has_newsletter: boolean;
  tags: number[];
  gender: string;
  sales_id?: Identifier;
  /** Legacy: kept in the database, no longer written by the app. */
  status?: string;
  background: string;
  phone_jsonb: PhoneNumberAndType[];
  nb_tasks?: number;
  company_name?: string;
  /** Apellido materno. */
  second_last_name?: string | null;
  /** True when the surname split is not certain and a person should review it. */
  name_needs_review?: boolean;
  contact_types?: ContactType[];
  /** Origen (`lead_sources.id`). */
  lead_source_id?: Identifier | null;
  /** The referring contact when the origin has `link_kind` 'contact'. */
  referred_by_contact_id?: Identifier | null;
  /** The colega contact when the origin has `link_kind` 'colega'. */
  origin_colega_contact_id?: Identifier | null;
  business_unit?: BusinessUnit | null;
  /** The BPO project (`projects.id`), only when `business_unit` is 'bpo'. */
  business_project_id?: Identifier | null;
  /** The tenant contact an Aval guarantees; only for the 'guarantor' type. */
  guarantor_of_contact_id?: Identifier | null;
  colega_kind?: ColegaKind | null;
  colega_affiliation?: ColegaAffiliation | null;
  colega_agreement_signed?: boolean | null;
  /** Proveedor category (`supplier_categories.id`); only for the 'supplier' type. */
  supplier_category_id?: Identifier | null;
  /** Extension point: the Desarrollador link, nothing writes it yet. */
  developer_id?: Identifier | null;
  /** Summary-only (`contacts_summary`) joined names, never written. */
  lead_source_name?: string | null;
  supplier_category_name?: string | null;
  business_project_name?: string | null;
} & Pick<RaRecord, "id">;

/** Almikia Epic EC2a: a Proveedor category (mirrors `public.supplier_categories`). */
export type SupplierCategory = {
  name: string;
  active: boolean;
  created_by?: Identifier | null;
  created_at?: string;
} & Pick<RaRecord, "id">;

export type ContactNote = {
  contact_id: Identifier;
  text: string;
  date: string;
  sales_id: Identifier;
  status: string;
  attachments?: AttachmentNote[];
} & Pick<RaRecord, "id">;

/**
 * A client (Almikia Epic E3): a person or company a lead ultimately becomes.
 * Column-name contract shared with TASK-017's SQL (`supabase/schemas/01_tables.sql`)
 * — keep field names and value sets in lockstep. Enum values stay Spanish
 * domain data, English identifiers per the project's english-only rule.
 */
export const CLIENT_TYPES = ["persona_fisica", "persona_moral"] as const;
export type ClientType = (typeof CLIENT_TYPES)[number];

export const MARITAL_STATUSES = [
  "soltero",
  "casado",
  "union_libre",
  "divorciado",
  "viudo",
] as const;
export type MaritalStatus = (typeof MARITAL_STATUSES)[number];

// Almikia Epic E3b, owner answer A8: marital property regime, required only
// when marital_status is 'casado'. Matches TASK-069's
// `clients_marital_property_regime_check` (supabase/schemas/01_tables.sql).
export const MARITAL_PROPERTY_REGIMES = [
  "sociedad_conyugal",
  "separacion_bienes",
] as const;
export type MaritalPropertyRegime = (typeof MARITAL_PROPERTY_REGIMES)[number];

export type Client = {
  created_at: string;
  client_type: ClientType;
  /** The name exactly as on the official ID; for persona_moral the legal company name. */
  full_legal_name: string;
  rfc?: string | null;
  curp?: string | null;
  birthday?: string | null;
  marital_status?: MaritalStatus | null;
  /** Required when `marital_status` is 'casado'; null/hidden otherwise. */
  marital_property_regime?: MaritalPropertyRegime | null;
  children_count?: number | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  contact_id?: Identifier | null;
  /** The owner. Defaults to the current user's sale id when omitted on create. */
  sales_id?: Identifier;
} & Pick<RaRecord, "id">;

/** Lead origin (Almikia Epic E2). Must match TASK-008's `deals_lead_type_check` constraint. */
export const LEAD_TYPES = ["bpo", "proactivo", "almikia", "colega"] as const;
export type LeadType = (typeof LEAD_TYPES)[number];

export type Deal = {
  name: string;
  company_id: Identifier;
  contact_ids: Identifier[];
  category: string;
  stage: string;
  description: string;
  amount: number;
  created_at: string;
  updated_at: string;
  archived_at?: string;
  expected_closing_date: string;
  sales_id: Identifier;
  index: number;
  /**
   * Almikia leads (Epic E2): the "deals" resource stays as-is, these columns
   * are all optional/nullable so existing rows are unaffected. Column-name
   * contract shared with TASK-008's SQL (`supabase/schemas/01_tables.sql`) —
   * keep field names and value sets in lockstep.
   */
  project_id?: Identifier | null;
  /** The client this lead ultimately became, if any (Almikia Epic E3). */
  client_id?: Identifier | null;
  lead_type?: LeadType | null;
  /** The external broker ("colega") name/contact; only meaningful when lead_type is 'colega'. */
  external_broker?: string | null;
  source?: string | null;
  /** Almikia Epic E2b: the lead's source tag (`lead_sources.id`); `source` stays as the legacy free text. */
  lead_source_id?: Identifier | null;
  /** Almikia Epic E2b: the referring colega (`colegas.id`); only for lead_type 'colega'. */
  colega_id?: Identifier | null;
  /** Lead score, 0-8. Must match TASK-008's `deals_score_check` constraint. */
  score?: number | null;
  posi_intent?: string | null;
  posi_budget?: string | null;
  posi_payment_method?: string | null;
  posi_decision_maker?: string | null;
  /**
   * Test deal of the DEMO project (batch ED). Always derived from the project
   * by a database trigger (a manual flip is overwritten); only direccion reads
   * these rows, and every figure excludes them.
   */
  is_test_data?: boolean;
} & Pick<RaRecord, "id">;

/** Almikia Epic E2b: a lead source catalogue row (mirrors `public.lead_sources`). */
export type LeadSource = {
  name: string;
  color?: string | null;
  active: boolean;
  meta_mapping?: string | null;
  created_at?: string;
  updated_at?: string;
  updated_by?: Identifier | null;
  /**
   * Almikia Epic EC2a: 'contact' (Referido) asks for the referring contact, 'colega' (Colega)
   * asks for the colega contact when chosen as a contact's origin. Null = no link asked.
   */
  link_kind?: "contact" | "colega" | null;
} & Pick<RaRecord, "id">;

/** Almikia Epic E9: how a received Meta webhook ended (`meta_webhook_events.result`). */
export type MetaWebhookResult =
  | "received"
  | "rejected_signature"
  | "processed"
  | "ignored"
  | "error";

/**
 * Almikia Epic E9: one received Meta webhook POST (`meta_webhook_events`, append-only,
 * direccion read). `raw_body` exists in the table but is never selected or rendered by the UI.
 */
export type MetaWebhookEvent = {
  received_at: string;
  signature_valid: boolean;
  result: MetaWebhookResult;
  error?: string | null;
  processed_at?: string | null;
} & Pick<RaRecord, "id">;

export type MetaSourceChoice =
  | "mapped_ad"
  | "mapped_campaign"
  | "mapped_form"
  | "default"
  | "none";
export type MetaProjectChoice = "form_mapping" | "none" | "not_sellable";
export type MetaAssignmentOutcome =
  | "assigned"
  | "no_candidates"
  | "skipped"
  | "rule_disabled";

/** Almikia Epic E9: the per-lead outcome of a processed webhook (`meta_lead_ingestions`). */
export type MetaLeadIngestion = {
  leadgen_id: string;
  event_id: Identifier;
  deal_id: Identifier;
  form_id?: string | null;
  campaign_id?: string | null;
  ad_id?: string | null;
  outcome: "created";
  source_choice: MetaSourceChoice;
  project_choice: MetaProjectChoice;
  assignment_outcome: MetaAssignmentOutcome;
  created_at: string;
} & Pick<RaRecord, "id">;

/** Almikia Epic E9: direccion-managed Meta form id -> project mapping (never deleted). */
export type MetaFormProject = {
  meta_form_id: string;
  project_id: Identifier;
  created_at?: string;
  updated_at?: string;
  updated_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/** Almikia Epic EM (TASK-067): ad platform of a Meta lead (`deal_meta_attribution.platform`). */
export type MetaPlatform = "facebook" | "instagram";
/** `deal_meta_attribution.lookup_status`: names unresolved / resolved / no ad id at all. */
export type MetaLookupStatus = "pendiente" | "ok" | "sin_anuncio";

/**
 * Almikia Epic EM (TASK-067): Meta origin of a lead (`deal_meta_attribution`, one row per lead,
 * ids and ad names only, no personal data). `id` mirrors `deal_id` (the primary key) so
 * react-admin can key the row.
 */
export type MetaAttribution = {
  deal_id: Identifier;
  ad_id: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  adset_id: string | null;
  adset_name: string | null;
  ad_name: string | null;
  platform: MetaPlatform | null;
  ad_title: string | null;
  ctwa_clid: string | null;
  leadgen_id: string | null;
  first_contact_at: string;
  lookup_status: MetaLookupStatus;
  lookup_attempts: number;
  last_lookup_at: string | null;
  last_lookup_error: string | null;
} & Pick<RaRecord, "id">;

/** The columns `meta_ad_lookup` returns for each refreshed lead (no ctwa_clid / leadgen_id). */
export type MetaAdLookupAttribution = Pick<
  MetaAttribution,
  | "deal_id"
  | "ad_id"
  | "campaign_id"
  | "campaign_name"
  | "adset_id"
  | "adset_name"
  | "ad_name"
  | "platform"
  | "ad_title"
  | "lookup_status"
  | "lookup_attempts"
  | "last_lookup_at"
  | "last_lookup_error"
>;

/** `meta_ad_lookup` response: one outcome per lead plus the refreshed attribution rows. */
export type MetaAdLookupResponse = {
  results: { deal_id: Identifier; outcome: string }[];
  attributions: MetaAdLookupAttribution[];
};

/** `set_deal_ctwa_clid()` outcome. */
export type SetCtwaClidResult = { outcome: "created" | "unchanged" | "set" };

export type MetaReportGroupBy = "campaign" | "adset" | "ad";

/** Almikia Epic EM (TASK-067): one row of `meta_campaign_report(p_from, p_to, p_group_by)`. */
export type MetaCampaignReportRow = {
  campaign_id: string | null;
  campaign_name: string | null;
  adset_id: string | null;
  adset_name: string | null;
  ad_id: string | null;
  ad_name: string | null;
  platform: MetaPlatform | null;
  leads: number;
  qualified_leads: number;
  appointments: number;
  reservations: number;
  sales: number;
  sale_value: number;
  qualified_rate: number | null;
  appointment_rate: number | null;
  sales_rate: number | null;
  median_first_response_seconds: number | null;
};

export type MetaReportParams = {
  /** Inclusive Mexico City day, `YYYY-MM-DD`. */
  from: string;
  /** Inclusive Mexico City day, `YYYY-MM-DD`. */
  to: string;
  groupBy?: MetaReportGroupBy;
};

export type MetaCapiEventName = "lead_calificado" | "cita" | "compra";
export type MetaCapiStatus = "pending" | "sent" | "failed" | "abandoned";
export type MetaCapiLogStatus =
  | "sent"
  | "failed"
  | "abandoned"
  | "sin_configuracion"
  | "reintento";

/** Almikia Epic EM (TASK-067): one Conversions API event to send (`meta_capi_outbox`). */
export type MetaCapiOutboxEvent = {
  deal_id: Identifier;
  event_name: MetaCapiEventName;
  event_id: string;
  action_source: "system_generated" | "business_messaging";
  event_time: string;
  value: number | null;
  currency: string;
  operation_id: string | null;
  status: MetaCapiStatus;
  attempts: number;
  next_attempt_at: string;
  created_at: string;
  updated_at: string;
} & Pick<RaRecord, "id">;

/** `meta_capi_events_summary` row: the outbox row plus its lead label and last attempt. */
export type MetaCapiEventSummary = MetaCapiOutboxEvent & {
  lead_label: string;
  last_log_status: MetaCapiLogStatus | null;
  last_http_status: number | null;
  last_error: string | null;
  last_attempt_at: string | null;
};

/** Almikia Epic EM (TASK-067): one send attempt (`meta_capi_log`, append-only, no personal data). */
export type MetaCapiLogEntry = {
  outbox_id: Identifier;
  event_id: string;
  attempt_number: number;
  status: MetaCapiLogStatus;
  http_status: number | null;
  response_body: string | null;
  error: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
} & Pick<RaRecord, "id">;

export type MetaCapiEventFilters = {
  status?: MetaCapiStatus;
  eventName?: MetaCapiEventName;
  /** Inclusive Mexico City days, `YYYY-MM-DD`, applied to `event_time`. */
  from?: string;
  to?: string;
  page?: number;
  perPage?: number;
};

export type MetaPage = { page?: number; perPage?: number };
export type MetaPagedResult<T> = { data: T[]; total: number };

/** Almikia Epic EC: the corretaje split case codes (docs/almikia/product-rules.md). */
export type CorretajeCaseCode =
  | "1"
  | "1b"
  | "2"
  | "3"
  | "3b"
  | "4"
  | "4b"
  | "5";

/** Almikia Epic EC: a corretaje commission scheme row (mirrors `public.corretaje_commission_schemes`). */
export type CorretajeCommissionScheme = {
  case_code: CorretajeCaseCode;
  /** Percentages are 0-100. */
  colega_pct: number;
  vendedora_pct: number;
  opcionadora_pct: number;
  almikia_pct: number;
  /** 'total' applies the percentages to C; 'half' applies them to M = C minus the colega amount. */
  base: "total" | "half";
  /** ISO date (YYYY-MM-DD). */
  valid_from: string;
  valid_to?: string | null;
  created_by?: Identifier | null;
  created_at?: string;
  updated_at?: string;
  updated_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/** Almikia Epic EC: result of `public.corretaje_commission_split` (same column names). */
export type CorretajeSplitResult = {
  case_code: CorretajeCaseCode;
  scheme_id: Identifier;
  colega_amount: number;
  vendedora_amount: number;
  opcionadora_amount: number;
  almikia_amount: number;
};

/** Almikia Epic EC: status value set of `public.property_options.status` (manual, never driven by operations). */
export const PROPERTY_OPTION_STATUSES = [
  "disponible",
  "apartada",
  "vendida",
  "retirada",
] as const;
export type PropertyOptionStatus = (typeof PROPERTY_OPTION_STATUSES)[number];

/**
 * Almikia Epic EC2d: the seed list of non-archived `public.property_types` codes (the
 * deploy-time seed; 'otro' stays archived). The live set is the `property_types` catalogue,
 * which Direccion extends, so a type is any catalogue code.
 */
export const PROPERTY_OPTION_TYPES = [
  "casa",
  "departamento",
  "oficina",
  "terreno",
  "rancho",
  "bodega",
  "local",
  "lote_habitacional",
  "lote_comercial",
  "lote_industrial",
] as const;
/** A `property_types.code`; a string because Direccion creates new types at runtime. */
export type PropertyOptionType = string;

/** Almikia Epic EC2d: a `public.property_types` catalogue row (archived, never deleted). */
export type PropertyType = {
  /** Stable key referenced by `property_options.property_type`; assigned from the label, immutable. */
  code: string;
  label: string;
  position: number;
  archived_at?: string | null;
  created_at?: string;
  updated_at?: string;
  updated_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/** Almikia Epic EC2d: origin of an inventory row ('bpo' is virtual, never stored in `property_options`). */
export const INVENTORY_ORIGINS = ["opcion", "exclusiva", "bpo"] as const;
export type InventoryOrigin = (typeof INVENTORY_ORIGINS)[number];
export type PropertyOptionOrigin = Exclude<InventoryOrigin, "bpo">;
export type PropertyOperation = "venta" | "renta";
export type PropertyFurnished = "amueblado" | "sin_amueblar";
export type PropertyLonaKind = "se_vende" | "se_renta";

/** Almikia Epic EC2d: the promotion checklist of a listing (same columns on options and the inventory view). */
export type PropertyPromotion = {
  lona_placed: boolean;
  /** Set exactly when `lona_placed`. */
  lona_kind: PropertyLonaKind | null;
  photos_professional: boolean;
  video: boolean;
  virtual_tour: boolean;
  published_portals: boolean;
  /** Non-empty exactly when `published_portals`. */
  portals: string[];
  published_social: boolean;
  ficha_ready: boolean;
};

/** Defaults the database applies to the EC2d columns an older form does not send. */
export const getPropertyOptionDefaults = (): Omit<
  PropertyOption,
  | "id"
  | "address"
  | "zone"
  | "property_type"
  | "price"
  | "status"
  | "responsable_sales_id"
  | "responsable_is_amk"
  | "added_on"
  | "created_by"
  | "created_at"
  | "updated_at"
  | "updated_by"
> => ({
  origin: "opcion",
  exclusivity_signed_on: null,
  exclusivity_ends_on: null,
  operation: "venta",
  furnished: null,
  levels: null,
  bedrooms: null,
  bathrooms: null,
  area_m2: null,
  parking: null,
  lona_placed: false,
  lona_kind: null,
  photos_professional: false,
  video: false,
  virtual_tour: false,
  published_portals: false,
  portals: [],
  published_social: false,
  ficha_ready: false,
});

/**
 * Almikia Epic EC: a property outside the BPO projects (mirrors `public.property_options`).
 * The responsable is either an advisor (`responsable_sales_id`) or AMK (`responsable_is_amk`);
 * exactly one of the two is set. An option is withdrawn ('retirada'), never deleted.
 * Epic EC2d adds the origin, exclusivity dates, operation, characteristics and promotion flags.
 */
export type PropertyOption = {
  address: string;
  zone?: string | null;
  property_type: PropertyOptionType;
  price: number;
  status: PropertyOptionStatus;
  responsable_sales_id: Identifier | null;
  responsable_is_amk: boolean;
  /** ISO date (YYYY-MM-DD). */
  added_on: string;
  created_by?: Identifier | null;
  created_at?: string;
  updated_at?: string;
  updated_by?: Identifier | null;
  origin: PropertyOptionOrigin;
  /** Required when `origin` is 'exclusiva'. ISO date. */
  exclusivity_signed_on: string | null;
  /** Not before `exclusivity_signed_on`. ISO date. */
  exclusivity_ends_on: string | null;
  operation: PropertyOperation;
  /** Set exactly when `operation` is 'renta'. */
  furnished: PropertyFurnished | null;
  levels: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  area_m2: number | null;
  parking: number | null;
} & PropertyPromotion &
  Pick<RaRecord, "id">;

/**
 * Almikia Epic EC2d: a row of the read-only `public.inventory_items` view (stored options plus
 * the BPO units of non-demo projects). Never carries owner or commission data. FakeRest
 * emulates it (`inventoryItemsProvider.ts`) instead of storing it in `Db`.
 */
export type InventoryItem = {
  /** 'option-<id>' or 'unit-<id>'. */
  id: string;
  source: "option" | "unit";
  source_id: Identifier;
  origin: InventoryOrigin;
  operation: PropertyOperation;
  furnished: PropertyFurnished | null;
  property_type: PropertyOptionType;
  address: string;
  zone: string | null;
  price: number | null;
  status: string;
  levels: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  area_m2: number | null;
  parking: number | null;
  responsable_sales_id: Identifier | null;
  responsable_is_amk: boolean;
  project_id: Identifier | null;
  project_name: string | null;
  /** ISO date (YYYY-MM-DD). */
  added_on: string;
  days_in_inventory: number;
} & PropertyPromotion;

/** Almikia Epic EC: the owner's private contact, 1:1 with an option (mirrors `public.property_option_owners`). */
export type PropertyOptionOwner = {
  option_id: Identifier;
  full_name: string;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  created_by?: Identifier | null;
  created_at?: string;
  updated_at?: string;
  updated_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/** Almikia Epic E2b: an external broker who refers leads (mirrors `public.colegas`). */
export type Colega = {
  full_name: string;
  /** Exactly 10 digits. Third-party personal data: commercial roles only. */
  phone: string;
  /** The inmobiliaria name, or the literal 'independiente'. */
  brokerage: string;
  email?: string | null;
  notes?: string | null;
  active: boolean;
  /** Almikia Epic EC2a: the Colega contact this legacy row was migrated to. */
  contact_id?: Identifier | null;
  created_by?: Identifier | null;
  created_at?: string;
} & Pick<RaRecord, "id">;

export type DealNote = {
  deal_id: Identifier;
  text: string;
  date: string;
  sales_id: Identifier;
  attachments?: AttachmentNote[];
  /**
   * Almikia Epic EA (DATA CONTRACT EA (4)): set together, never one without
   * the other -- a `DealNote` row with both set is an "activity log entry".
   * Immutable after insert.
   */
  activity_type_id?: Identifier | null;
  activity_result_id?: Identifier | null;

  // This is defined for compatibility with `ContactNote`
  status?: undefined;
} & Pick<RaRecord, "id">;

export type Tag = {
  id: number;
  name: string;
  color: string;
};

export type Task = {
  contact_id: Identifier;
  type: string;
  text: string;
  due_date: string;
  done_date?: string | null;
  sales_id?: Identifier;
  /**
   * Almikia Epic EA (DATA CONTRACT EA (4)): set together for a lead's
   * "next-step task" created by `logDealActivity`; both immutable after
   * insert. Absent/NULL for a plain manual task.
   */
  deal_id?: Identifier | null;
  activity_type_id?: Identifier | null;
} & Pick<RaRecord, "id">;

export type ActivityCompanyCreated = {
  type: typeof COMPANY_CREATED;
  company_id: Identifier;
  company: Company;
  sales_id: Identifier;
  date: string;
} & Pick<RaRecord, "id">;

export type ActivityContactCreated = {
  type: typeof CONTACT_CREATED;
  company_id: Identifier;
  sales_id?: Identifier;
  contact: Contact;
  date: string;
} & Pick<RaRecord, "id">;

export type ActivityContactNoteCreated = {
  type: typeof CONTACT_NOTE_CREATED;
  sales_id?: Identifier;
  contactNote: ContactNote;
  date: string;
} & Pick<RaRecord, "id">;

export type ActivityDealCreated = {
  type: typeof DEAL_CREATED;
  company_id: Identifier;
  sales_id?: Identifier;
  deal: Deal;
  date: string;
};

export type ActivityDealNoteCreated = {
  type: typeof DEAL_NOTE_CREATED;
  sales_id?: Identifier;
  dealNote: DealNote;
  date: string;
};

export type Activity = RaRecord &
  (
    | ActivityCompanyCreated
    | ActivityContactCreated
    | ActivityContactNoteCreated
    | ActivityDealCreated
    | ActivityDealNoteCreated
  );

export interface RAFile {
  src: string;
  title: string;
  path?: string;
  rawFile: File;
  type?: string;
}

export type AttachmentNote = RAFile;

export interface LabeledValue {
  value: string;
  label: string;
}

/**
 * Almikia Epic EP (TASK-104): the app's `dealStages` is the effective stage
 * list of DATA CONTRACT EP (8) -- every field below is OPTIONAL so an
 * existing `DealStage` consumer (which only ever read `value`/`label`)
 * compiles unchanged. Populated for the resolved list built from
 * `deal_stage_configs` (see `deals/stageConfig.ts#resolveEffectiveDealStages`),
 * absent for a plain `{ value, label }` fallback list.
 */
export type DealStage = LabeledValue & {
  placement?: StagePlacement;
  /** Display order inside its `placement`, ascending, ties broken by `key`. */
  position?: number;
  archived?: boolean;
  isSystem?: boolean;
  /** Informational only in EP: no SLA, alert or block reads it (epic ER will). */
  targetHours?: number | null;
  requiredActivities?: string[];
};

export interface NoteStatus extends LabeledValue {
  color: string;
}

export interface ContactGender {
  value: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

/**
 * Catalogue: developers, projects, units (Almikia Epic E1).
 * Canonical data contract shared with the SQL schema (TASK-001) — keep
 * field names and value sets in lockstep with `supabase/schemas/01_tables.sql`.
 */

export const PROJECT_CODES = [
  "BEA",
  "LIS",
  "DIA",
  "MAZ",
  "AUN",
  "BEL",
  // Fictitious DEMO project (batch ED); the SQL `projects_code_check` accepts it.
  "DEMO",
] as const;
export type ProjectCode = (typeof PROJECT_CODES)[number];

/**
 * Shared two-state value set ('pendiente' | 'validado') used both by a
 * developer's authorization to sell and a project's compliance pack.
 */
const CATALOGUE_STATUSES = ["pendiente", "validado"] as const;
export type CatalogueStatus = (typeof CATALOGUE_STATUSES)[number];
export const AUTHORIZATION_TO_SELL_STATUSES = CATALOGUE_STATUSES;
export const COMPLIANCE_PACK_STATUSES = CATALOGUE_STATUSES;

export const UNIT_TYPES = ["depto", "local", "lote"] as const;
export type UnitType = (typeof UNIT_TYPES)[number];

export const UNIT_STATUSES = ["disponible", "apartada", "vendida"] as const;
export type UnitStatus = (typeof UNIT_STATUSES)[number];

export type Developer = {
  legal_name: string;
  // ASSUMPTION (Almikia Epic E1b, TASK-028): these five legacy fiscal fields
  // are superseded by `DeveloperFiscalProfile` (public.developer_fiscal_profiles,
  // TASK-027) but stay declared here, deprecated, until a later cleanup epic.
  // Removing them now would make the deploy-time migration round derive a
  // `DROP COLUMN` on `public.developers`, destroying the data the one-time
  // copy (TASK-027) migrates into the new table.
  /** @deprecated Use `DeveloperFiscalProfile.rfc` instead. */
  rfc?: string | null;
  /** @deprecated Use `DeveloperFiscalProfile.fiscal_zip_code` instead. */
  fiscal_zip_code?: string | null;
  /** @deprecated Use `DeveloperFiscalProfile.tax_regime` instead. */
  tax_regime?: string | null;
  /** @deprecated Use `DeveloperFiscalProfile.cfdi_use` instead. */
  cfdi_use?: string | null;
  /** @deprecated Use `DeveloperFiscalProfile.invoice_email` instead. */
  invoice_email?: string | null;
  authorization_to_sell_status: CatalogueStatus;
  created_at: string;
} & Pick<RaRecord, "id">;

/**
 * 1:1 fiscal profile for a developer (Almikia Epic E1b, TASK-027/028).
 * Column contract shared verbatim with the `public.developer_fiscal_profiles`
 * SQL migration (TASK-027): all fiscal columns are nullable.
 */
export type DeveloperFiscalProfile = {
  created_at: string;
  developer_id: Identifier;
  rfc?: string | null;
  legal_name?: string | null;
  tax_regime?: string | null;
  fiscal_zip_code?: string | null;
  cfdi_use?: string | null;
  invoice_email?: string | null;
} & Pick<RaRecord, "id">;

/**
 * One row per uploaded version of a developer's Constancia de Situacion
 * Fiscal (Almikia Epic E1b, TASK-027/028). Rows are only ever soft-deleted
 * (`deleted_at`), never removed, to preserve the upload/audit trail.
 */
export type DeveloperFiscalDocument = {
  developer_id: Identifier;
  storage_path: string;
  uploaded_by: Identifier;
  uploaded_at: string;
  sha256?: string | null;
  deleted_at?: string | null;
} & Pick<RaRecord, "id">;

/** Kind of a sales tool; matches the `sales_tools_kind_check` values. */
export type SalesToolKind =
  | "script"
  | "question"
  | "video"
  | "brochure"
  | "comparison"
  | "fact_sheet"
  | "objection";

/**
 * A reusable piece of sales guidance (Almikia Epic EH, `public.sales_tools`).
 * Text-only tools carry `body`; a link tool carries `video_url`; a file tool
 * carries the three `storage_path`/`file_name`/`mime_type` columns together
 * (private `sales-tools` bucket). Empty tag arrays mean "applies to all".
 * Archived (`archived_at`), never deleted.
 */
export type SalesToolFileMetadata = {
  storage_path: string;
  file_name: string;
  mime_type: string;
};

export type SalesTool = {
  title: string;
  kind: SalesToolKind;
  body?: string | null;
  video_url?: string | null;
  storage_path?: string | null;
  file_name?: string | null;
  mime_type?: string | null;
  stage_keys: string[];
  project_ids: Identifier[];
  buyer_type_ids: Identifier[];
  archived_at?: string | null;
  created_by?: Identifier | null;
  updated_by?: Identifier | null;
  created_at: string;
  updated_at: string;
} & Pick<RaRecord, "id">;

export type Project = {
  name: string;
  code: ProjectCode;
  developer_id: Identifier;
  compliance_pack_status: CatalogueStatus;
  invoice_concept_template: string;
  invoice_concept_template_version: number;
  /** Almikia Epic E6: developer commission % (receivable default); null until direccion sets it. */
  developer_commission_pct?: number | null;
  // Almikia Epic E3b-2 ("Archive Mazarik"): hidden for new leads and
  // operations once true, history kept, never deleted (see
  // `isProjectSellable` / `SELLABLE_PROJECTS_FILTER`).
  archived: boolean;
  /**
   * Fictitious DEMO project (demo isolation, batch ED). Visible and writable
   * only by direccion; the database enforces it, the UI only labels it.
   */
  is_demo?: boolean;
  created_at: string;
} & Pick<RaRecord, "id">;

export type Unit = {
  project_id: Identifier;
  identifier: string;
  type: UnitType;
  status: UnitStatus;
  list_price?: number | null;
  created_at: string;
} & Pick<RaRecord, "id">;

/**
 * Team roster / advisor-project membership (Almikia Epic E3b-2, TASK-079 to
 * TASK-086). Column-name contract shared verbatim with the SQL schema
 * (`public.team_roster`, `public.team_roster_projects`, `public.sales_projects`)
 * -- keep field names and value sets in lockstep. `team_roster` is a reference
 * directory seeded from the Equipo table (never creates `Sale` rows);
 * `sales_projects` is the LIVE advisor <-> project N:N membership, the only
 * one RLS helpers read (see `adr/ADR-ee2f59a9-TASK-079-team-roster-and-sales-projects.md`).
 */
export const TEAM_ROSTER_MATCH_STATUSES = [
  "unmatched",
  "pending",
  "confirmed",
] as const;
export type TeamRosterMatchStatus = (typeof TEAM_ROSTER_MATCH_STATUSES)[number];

/**
 * Match lifecycle (data contract item 6): 'unmatched' -> 'pending' (a live
 * user claims the row: her `advisor_code` equals the roster clave, or her
 * email equals the roster email) -> 'confirmed' (direccion or a
 * lead_manager confirms); 'pending' -> 'unmatched' on rejection or when the
 * claimant's clave changes away. Invariant: `sales_id` is set exactly when
 * `match_status` is not 'unmatched'.
 */
export type TeamRosterEntry = {
  /** Natural seed key from the Equipo table. */
  full_name: string;
  /** UI label "Clave". Nullable, unique when set. */
  advisor_code?: string | null;
  /** The Equipo "Puesto" verbatim. */
  position: string;
  /** Nullable: NULL when the Puesto has no 1:1 app role. */
  role?: AppRole | null;
  business_arm?: BusinessArm | null;
  /** The acompañamiento reference (self-FK to another `team_roster` row). */
  reports_to_roster_id?: Identifier | null;
  /** Unknown today for most rows (owner item P8, docs/almikia/QUESTIONS.md). */
  email?: string | null;
  /** The claiming / confirmed live user. Nullable, unique when set. */
  sales_id?: Identifier | null;
  match_status: TeamRosterMatchStatus;
  /** Set once when the person leaves the team; never cleared. */
  departed_at?: string | null;
  claimed_at?: string | null;
  confirmed_at?: string | null;
  confirmed_by?: Identifier | null;
  created_at: string;
} & Pick<RaRecord, "id">;

/**
 * A roster's seeded project membership (reference only, never the live
 * scope). Real PK is the composite (`team_roster_id`, `project_id`); `id`
 * here is a synthetic identifier the FakeRest/react-admin layer requires.
 */
export type TeamRosterProject = {
  team_roster_id: Identifier;
  project_id: Identifier;
  /** She is a Líder BPO of that project (seeded/reference side). */
  is_lider_for_project: boolean;
} & Pick<RaRecord, "id">;

/**
 * The LIVE advisor <-> project N:N membership. At most one ACTIVE
 * (`ended_at` IS NULL) row per (`sales_id`, `project_id`); no count rule of
 * any kind (a project may have several líderes, an advisor several
 * projects). A membership ends by setting `ended_at`, never by delete.
 */
export type SalesProject = {
  sales_id: Identifier;
  project_id: Identifier;
  is_lider_for_project: boolean;
  created_at: string;
  ended_at?: string | null;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic E7 (DATA CONTRACT E7, TASK-087/TASK-088). One catalogue row
 * per incentive, never overlapping in `[valid_from, valid_to]` for the same
 * `code`. Incentive values are DATA read from here, never hardcoded in UI or
 * calculation logic (Epic E7 hard constraint).
 */
export const INCENTIVE_PROGRAM_CODES = [
  "monthly_sale_incentive",
  "quarterly_outstanding_sales",
  "monthly_proactive_appointments",
  "best_bpo_team",
  "gasoline_insurance",
] as const;
export type IncentiveProgramCode = (typeof INCENTIVE_PROGRAM_CODES)[number];

export const INCENTIVE_PROGRAM_SCOPES = ["advisor", "team"] as const;
export type IncentiveProgramScope = (typeof INCENTIVE_PROGRAM_SCOPES)[number];

export const INCENTIVE_PROGRAM_PERIODS = [
  "month",
  "quarter",
  "accumulated",
] as const;
export type IncentiveProgramPeriod = (typeof INCENTIVE_PROGRAM_PERIODS)[number];

/** `parameters` shape for `code: 'monthly_sale_incentive'`. */
export type MonthlySaleIncentiveParameters = {
  rate_pct: number;
  cap_mxn_per_sale: number;
  /** The level catalogue itself arrives with a later epic (E6); 'junior' today. */
  min_level: string;
};

export type QuarterlyOutstandingSalesTier = {
  threshold_mxn: number;
  /** true = threshold reached at `>=`; false = strictly `>` (DOMAIN RULE (f)). */
  inclusive: boolean;
  rate_pct: number;
};

/** `parameters` shape for `code: 'quarterly_outstanding_sales'`. `tiers` is ascending by `threshold_mxn`. */
export type QuarterlyOutstandingSalesParameters = {
  min_units: number;
  tiers: QuarterlyOutstandingSalesTier[];
};

/** `parameters` shape for `code: 'monthly_proactive_appointments'`. */
export type MonthlyProactiveAppointmentsParameters = {
  target_appointments: number;
  reward_max_mxn: number;
};

/** `code: 'best_bpo_team'` / `'gasoline_insurance'` define no parameters yet. */
export type EmptyIncentiveProgramParameters = Record<string, never>;

type IncentiveProgramBase = {
  /** Spanish display name. */
  name: string;
  scope: IncentiveProgramScope;
  period: IncentiveProgramPeriod;
  direct_sales_only: boolean;
  /** Inclusive start date (YYYY-MM-DD). */
  valid_from: string;
  /** Inclusive end date; NULL = open-ended. */
  valid_to?: string | null;
  created_at: string;
  updated_at: string;
} & Pick<RaRecord, "id">;

/**
 * Discriminated by `code` (DATA CONTRACT E7): each code has its own
 * `parameters` JSON shape.
 */
export type IncentiveProgram =
  | ({
      code: "monthly_sale_incentive";
      parameters: MonthlySaleIncentiveParameters;
    } & IncentiveProgramBase)
  | ({
      code: "quarterly_outstanding_sales";
      parameters: QuarterlyOutstandingSalesParameters;
    } & IncentiveProgramBase)
  | ({
      code: "monthly_proactive_appointments";
      parameters: MonthlyProactiveAppointmentsParameters;
    } & IncentiveProgramBase)
  | ({
      code: "best_bpo_team";
      parameters: EmptyIncentiveProgramParameters;
    } & IncentiveProgramBase)
  | ({
      code: "gasoline_insurance";
      parameters: EmptyIncentiveProgramParameters;
    } & IncentiveProgramBase);

export const ADVISOR_GOAL_SCOPES = ["advisor", "team"] as const;
export type AdvisorGoalScope = (typeof ADVISOR_GOAL_SCOPES)[number];

/**
 * Almikia Epic E7 (DATA CONTRACT E7). `sales_id` is the advisor for
 * `scope: 'advisor'`, the Líder BPO whose team is measured for
 * `scope: 'team'`. Both targets NULL means no goal is set (DOMAIN RULE (g):
 * shown WITHOUT a target, never as 0% or a shortfall). `set_by` is forced to
 * the caller's sales id on every write (anti-spoofing, never client-trusted).
 */
export type AdvisorGoal = {
  sales_id: Identifier;
  scope: AdvisorGoalScope;
  /** First day of the goal's month (YYYY-MM-DD). */
  period_month: string;
  /** Direct sale count target. */
  target_units?: number | null;
  /** Direct sale amount target, MXN, same unit as `Deal.amount`. */
  target_amount_mxn?: number | null;
  /** Almikia Epic EC2d: monthly target of properties captured (options and exclusivas). */
  target_captures?: number | null;
  set_by?: Identifier | null;
  created_at: string;
  updated_at: string;
} & Pick<RaRecord, "id">;

/**
 * A row of `public.lider_team_members` (DATA CONTRACT E7): a security-invoker
 * view over `sales`, returning zero rows for any caller who isn't a `lider`.
 * FakeRest emulates it per-identity instead of storing it in `Db` (mirrors
 * `private.in_lider_team()`, see `incentives.ts`).
 */
export type LiderTeamMember = {
  first_name: string;
  last_name: string;
  advisor_code?: string | null;
  avatar?: RAFile;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic EO (DATA CONTRACT EO (1)): a row of the live
 * `public.org_chart_people` view, computed from `sales` + `sales_projects` +
 * `projects` on every read -- FakeRest emulates this the same way in
 * `orgChartProvider.ts`, never storing a copy in `Db`. `advisor_code` carries
 * the real clave only for a direccion/lead_manager caller, NULL on every row
 * for every other role (including her own row); `reports_to` is NULL when
 * its target isn't itself a row of this view (disabled or an external role).
 * NO `nivel` column (DATA CONTRACT EO ASSUMPTION); `phone` is direccion/lead_manager only.
 */
export type OrgChartPerson = {
  first_name: string;
  last_name: string;
  email: string;
  avatar?: RAFile;
  role: AppRole;
  business_arm?: BusinessArm | null;
  reports_to?: Identifier | null;
  advisor_code?: string | null;
  /** E.164; non-null only for direccion and lead_manager callers (and only when loaded). */
  phone?: string | null;
  /** Computed display level (EC2c); absent/null when the catalogue has none. */
  level_code?: string | null;
  level_name?: string | null;
  /** True for a legacy 'ambos' person until Direccion picks Corretaje or BPO. */
  business_unit_needs_review?: boolean;
  /** Active memberships only, ordered by project name; `[]` when none. */
  projects: OrgChartPersonProject[];
} & Pick<RaRecord, "id">;

/** One of `OrgChartPerson.projects`' active, non-archived-project memberships. */
export type OrgChartPersonProject = {
  project_id: Identifier;
  code: ProjectCode;
  name: string;
  is_lider_for_project: boolean;
};

/**
 * Almikia Epic EO (DATA CONTRACT EO (2)): a culture document's stable
 * identity. Its content lives in `CultureDocumentVersion` (append-only);
 * `created_by` is NULL for a seeded document, otherwise the direccion user
 * who created it.
 */
export type CultureDocument = {
  slug: string;
  sort_order: number;
  created_at: string;
  created_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic EO (DATA CONTRACT EO (3)): one append-only content revision of
 * a `CultureDocument`. `version`/`created_by` are server-assigned on create
 * (FakeRest parity in `orgChartProvider.ts`), never client-trusted; a version
 * row is never updated or deleted -- editing a document is always a new row
 * with `version = previous max + 1`.
 */
export type CultureDocumentVersion = {
  document_id: Identifier;
  version: number;
  title: string;
  body_markdown: string;
  created_at: string;
  created_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic EO (DATA CONTRACT EO (4)): the latest version of each
 * `CultureDocument`, computed on every read (never stored). `id` is the
 * document id, not the version id; consumers sort by `sort_order`, then `id`.
 */
export type CultureDocumentCurrent = {
  slug: string;
  sort_order: number;
  version: number;
  title: string;
  body_markdown: string;
  updated_at: string;
  updated_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic EP (DATA CONTRACT EP (1)): where a `DealStageConfig`'s key is
 * displayed. `'inbox'` is allowed ONLY for the `'prospecto'` key and is
 * immutable after insert, mirroring the DB CHECK constraint.
 */
export const STAGE_PLACEMENTS = ["inbox", "kanban", "special"] as const;
export type StagePlacement = (typeof STAGE_PLACEMENTS)[number];

/**
 * Almikia Epic EP (DATA CONTRACT EP (2)): the only criterion implemented in
 * EP. A future criterion is a new code plus its evaluator in SQL and TS, not
 * a schema reshape — never invent another code here.
 */
export const STAGE_CRITERION_CODES = ["posi_complete"] as const;
export type StageCriterionCode = (typeof STAGE_CRITERION_CODES)[number];

/**
 * Almikia Epic EP (DATA CONTRACT EP (1)): the configuration layer over
 * `deals.stage` — `deals.stage` itself stays free text (no FK, no CHECK,
 * never rewritten); this table only configures HOW a stage value is
 * labeled, ordered, displayed and gated. `key` is immutable after insert
 * (the value stored in `deals.stage`); `placement`/`is_system` are also
 * immutable after insert. `required_activities` is a PLACEHOLDER
 * (displayed only, never enforced, never a criterion) until epic EA's
 * activity catalogue.
 */
export type DealStageConfig = {
  key: string;
  label: string;
  placement: StagePlacement;
  /** Display order inside its `placement`, ascending, ties broken by `key`. */
  position: number;
  /** Informational only in EP: no SLA, alert or block reads it (epic ER will). */
  target_hours?: number | null;
  required_activities: string[];
  is_system: boolean;
  /** Archive = set, unarchive = clear; a system row can never be archived. */
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
  updated_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic EP (DATA CONTRACT EP (2)): a criterion attached to a stage —
 * when `enabled`, it must be met to ENTER that stage (an insert with that
 * stage, or an update that changes `stage` to it). On update only `enabled`
 * (plus the server-assigned `updated_*`) may change: `stage_key` and
 * `criterion_code` are immutable after insert.
 */
export type DealStageCriterion = {
  stage_key: string;
  criterion_code: StageCriterionCode;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  updated_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic EA (DATA CONTRACT EA (1)): the activity catalogue --
 * `key` is the seed/fixture handle only (NULL for a direccion-created row,
 * never shown in the UI, immutable after insert); `name`/`message_template`/
 * `max_attempts`/`position`/`archived_at`/`updated_*` mirror
 * `public.deal_activity_types` verbatim.
 */
export type DealActivityType = {
  key?: string | null;
  name: string;
  /** The Spanish message the advisor copies while doing the activity; placeholders per DATA CONTRACT EA (6). */
  message_template?: string | null;
  /** The Código Azul-style attempt cap for this activity, counted per lead. */
  max_attempts?: number | null;
  /** Display order ascending, ties by id; a UI-created row gets max + 10. */
  position: number;
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
  updated_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic EA (DATA CONTRACT EA (2)): a possible result of an activity,
 * carrying the "next step and when" rule -- `next_activity_type_id` and
 * `next_step_offset_hours` are both set or both NULL (CHECK), `activity_type_id`
 * immutable after insert.
 */
export type DealActivityResult = {
  key?: string | null;
  activity_type_id: Identifier;
  label: string;
  message_template?: string | null;
  /** NULL = no next step; may equal `activity_type_id` (e.g. Código Azul retrying itself). */
  next_activity_type_id?: Identifier | null;
  /** Hours added to "now" for the next-step task's due date, 0-2160. */
  next_step_offset_hours?: number | null;
  position: number;
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
  updated_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic EA (DATA CONTRACT EA (3)): the OPTIONAL stage <-> catalogue
 * link (mirrors `DealStageCriterion`'s shape) -- unlink is `enabled: false`,
 * never a delete; on update only `enabled` (plus server-assigned `updated_*`)
 * may change.
 */
export type DealStageActivity = {
  stage_key: string;
  activity_type_id: Identifier;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  updated_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic EA (DATA CONTRACT EA (7)): the 5 outcomes of `logDealActivity`
 * -- `'created'` is the default (a next-step task was scheduled), the other
 * 4 are the exceptions where the entry is still logged but no task is
 * created, in the contract's evaluation order (`'none'` first, `'created'`
 * only when none of the others apply).
 */
export const DEAL_ACTIVITY_NEXT_STEP_STATUSES = [
  "created",
  "none",
  "no_contact",
  "attempts_exhausted",
  "next_activity_archived",
] as const;
export type DealActivityNextStepStatus =
  (typeof DEAL_ACTIVITY_NEXT_STEP_STATUSES)[number];

/** `logDealActivity`'s input (both data providers, DATA CONTRACT EA (7)). */
export type LogDealActivityInput = {
  dealId: Identifier;
  activityTypeId: Identifier;
  activityResultId: Identifier;
  /** Trimmed to at most 2000 chars server-side; optional. */
  note?: string;
  /** Overrides the result's default `next_step_offset_hours` due date when given. */
  nextDueAt?: string;
};

/** `logDealActivity`'s result (both data providers, DATA CONTRACT EA (7)). */
export type LogDealActivityResult = {
  noteId: Identifier;
  /** NULL when no next-step task was created. */
  taskId: Identifier | null;
  closedTaskIds: Identifier[];
  nextStep: DealActivityNextStepStatus;
};

/**
 * Almikia Epic EF (DATA CONTRACT EF (1)): the ONE identical shape shared by
 * the five buyer-profile option catalogues (`profile_buyer_types`,
 * `profile_credit_types`, `profile_timelines`, `profile_motivators`,
 * `profile_objections`) -- `key` is the seed/fixture handle only (NULL for a
 * direccion-created row, never shown in the UI, immutable after insert),
 * `label`/`position`/`archived_at`/`updated_*` mirror the SQL columns
 * verbatim. Archiving hides an option from a NEW selection; a profile that
 * already holds it keeps it.
 */
export type ProfileOption = {
  key?: string | null;
  label: string;
  /** Display order ascending, ties by id; a UI-created row gets max + 10. */
  position: number;
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
  updated_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic EF (DATA CONTRACT EF (2)): a lead's ('deal's) buyer profile,
 * 1:1, created lazily on the first save. There is NO "who decides" column
 * here -- 'Quién decide' is `Deal.posi_decision_maker` (E2 POSI), shown
 * read-only in the profile. Every amount is between 0 and 999,999,999.99
 * when set; `budget_min` <= `budget_max` when both are set. Array columns
 * hold no NULL element and no duplicate, at most 10 elements each, in
 * selection order.
 */
export type DealProfile = {
  /** Immutable after insert -- the parent lead, unique (1:1). */
  deal_id: Identifier;
  buyer_type_id?: Identifier | null;
  /** MXN. A single amount is stored as `budget_min` only. */
  budget_min?: number | null;
  /** MXN. */
  budget_max?: number | null;
  credit_type_id?: Identifier | null;
  /** MXN ("Enganche"). */
  down_payment?: number | null;
  /** MXN ("Mensualidad"). */
  monthly_payment?: number | null;
  /** Free text, trimmed non-blank when set, at most 120 chars. */
  zone?: string | null;
  /** Same value set as `Unit.type` / `UNIT_TYPES` -- reused, not copied. */
  property_type?: UnitType | null;
  /** 0-4; 0 = 'Estudio', 4 = '4 o más'. */
  bedrooms?: number | null;
  timeline_id?: Identifier | null;
  /** Elements = `ProfileOption` ids of `profile_motivators`. */
  motivator_ids: Identifier[];
  /** Elements = `ProfileOption` ids of `profile_objections`. */
  objection_ids: Identifier[];
  /** NULL = not asked yet. */
  sells_other_property?: boolean | null;
  interest_level?: ProfileInterestLevel | null;
  /** Elements = `Project` ids. */
  compared_project_ids: Identifier[];
  /** Trimmed non-blank when set, at most 2000 chars. */
  notes?: string | null;
  created_at: string;
  updated_at: string;
  updated_by?: Identifier | null;
} & Pick<RaRecord, "id">;

/** Almikia Epic EF (DATA CONTRACT EF (2)): "Recámaras" fixed value set. */
export const PROFILE_BEDROOM_OPTIONS = [0, 1, 2, 3, 4] as const;
export type ProfileBedrooms = (typeof PROFILE_BEDROOM_OPTIONS)[number];

/** Almikia Epic EF (DATA CONTRACT EF (2)): "Interés" fixed value set. */
export const PROFILE_INTEREST_LEVELS = ["alto", "medio", "bajo"] as const;
export type ProfileInterestLevel = (typeof PROFILE_INTEREST_LEVELS)[number];

/**
 * Almikia Epic ER (backlog "ER -- Alerts and Direccion funnel", vision #12,
 * #13): an append-only log of when a lead enters a stage -- one row on lead
 * create (its initial stage) and one row per stage CHANGE, written ONLY by
 * `public.log_deal_stage_entry()` (or its FakeRest parity) -- never
 * insertable/updatable/deletable through the API by any role, including
 * direccion. `is_baseline` marks the one-time deploy-time marker seeded for
 * every pre-existing lead (`supabase/seed.sql`) so a pre-existing lead's
 * clock starts at deploy time, never at its unrecorded creation date.
 */
export type DealStageEntry = {
  deal_id: Identifier;
  stage_key: string;
  entered_at: string;
  is_baseline: boolean;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic ER (backlog "ER -- Alerts and Direccion funnel", vision #12, TASK-003): the
 * 4 rule types the alert engine (TASK-005) knows -- a future type is a new code plus its SQL
 * evaluator, never a schema reshape. The 'cotizacion sin seguimiento' rule named in the
 * brief is NOT seeded/implemented: EQ's quoter does not exist on main.
 */
export const ALERT_RULE_TYPES = [
  "sin_seguimiento",
  "detenido_en_etapa",
  "cumple_criterio_sin_cita",
  "alta_intencion_sin_actividad",
] as const;
export type AlertRuleType = (typeof ALERT_RULE_TYPES)[number];

/**
 * Almikia Epic ER (TASK-003): the alert-rule catalogue -- `key` and `rule_type` are both
 * immutable after insert. Not every rule type uses every parameter field:
 * `hours_threshold` (`sin_seguimiento`/`alta_intencion_sin_actividad`, 1-2160),
 * `stuck_lead_count_threshold` (`detenido_en_etapa`, 1-100), `target_stage_key` +
 * `appointment_activity_type_id` (`cumple_criterio_sin_cita` only). INSERT and DELETE are
 * rejected for every role including direccion -- only `enabled`/`label`/the parameter
 * columns may change, direccion only.
 */
export type AlertRule = {
  key: string;
  rule_type: AlertRuleType;
  label: string;
  enabled: boolean;
  hours_threshold?: number | null;
  stuck_lead_count_threshold?: number | null;
  target_stage_key?: string | null;
  appointment_activity_type_id?: Identifier | null;
  created_at: string;
  updated_at: string;
  updated_by?: Identifier | null;
  /**
   * Almikia Epic ER (TASK-005): throttle bookkeeping for
   * `evaluate_lead_alerts()`/its FakeRest parity -- set on every rule row at
   * the end of a full evaluation run, never shown in any UI. `null` until the
   * engine has run for the first time.
   */
  last_evaluated_at?: string | null;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic ER (TASK-003): one alert raised on a lead -- always about a LEAD, never an
 * advisor. `resolved_at: null` means the alert is OPEN; at most one open alert exists per
 * rule + lead. Never deleted, never written directly by any role -- only the TASK-005 engine
 * writes it (SECURITY DEFINER), so there is no create/edit/delete anywhere in the UI.
 */
export type LeadAlert = {
  rule_id: Identifier;
  deal_id: Identifier;
  raised_at: string;
  resolved_at?: string | null;
} & Pick<RaRecord, "id">;

/**
 * Almikia Epic ER (TASK-005): the result of `CrmDataProvider.evaluateLeadAlerts()` --
 * `ran: false` means the call landed inside the throttle window and did nothing; `ran: true`
 * means a full evaluation ran. Deliberately carries no lead data (NFR: security) -- reading
 * alerts always goes through `lead_alerts`' own RLS/FakeRest guard instead.
 */
export type EvaluateLeadAlertsResult = {
  ran: boolean;
};

/**
 * Almikia Epic ER (TASK-002, vision #13): the Direccion funnel, one row per
 * funnel stage (the non-archived inbox stage, kanban stages, then
 * `venta_cerrada`, in EP's configured order -- see `get_funnel_metrics()`,
 * `supabase/schemas/02_functions.sql`). `conversion_rate` and
 * `median_hours_in_stage` are `null` when there is not enough data to compute
 * them (zero leads reached the stage, or no completed stay); `conversion_rate`
 * is also `null` for the last stage (there is no "next" to advance to).
 */
export type FunnelStageMetric = {
  stage_key: string;
  reached_count: number;
  advanced_count: number;
  /** Percent, 0-100, one decimal. `null` when not computable (see above). */
  conversion_rate: number | null;
  median_hours_in_stage: number | null;
  /** Open leads (non-archived) currently in this stage past its `target_hours`. */
  stuck_open_count: number;
};

/**
 * Almikia Epic ER (TASK-002): `get_funnel_metrics()`'s full result.
 * `first_response_median_hours`/`contactability_rate` are direccion-only --
 * `null` for every other role, enforced server-side (Independence guide:
 * advisor activity/usage metrics are never visible to any other role), never
 * just hidden in the UI.
 */
export type FunnelMetrics = {
  stages: FunnelStageMetric[];
  first_response_median_hours: number | null;
  contactability_rate: number | null;
};

/**
 * Optional filters for `get_funnel_metrics()`, combined with AND. `advisorId`
 * is rejected server-side (Spanish error) for any caller who is not
 * direccion -- see `CrmDataProvider.getFunnelMetrics`.
 */
export type FunnelMetricsFilters = {
  advisorId?: Identifier | null;
  projectId?: Identifier | null;
  leadType?: string | null;
};

/**
 * Almikia Epic EI (AI copilot, TASK-003). Client-side mirror of `ai_settings`,
 * `ai_usage_log`, the `ai_usage_monthly` view, `deal_ai_summaries`,
 * `deal_ai_suggestions` (TASK-001 schema) and the `ai_copilot` edge function
 * contract (TASK-002). The browser never holds an API key: every real call goes
 * through the edge function.
 */
export const AI_ACTIONS = [
  "note_suggestions",
  "suggest_message",
  "whats_missing",
] as const;
export type AiAction = (typeof AI_ACTIONS)[number];

export type AiSuggestionKind = "note_chips" | "missing_items" | "message";
export type AiSuggestionStatus =
  | "pending"
  | "accepted"
  | "edited"
  | "discarded";

/** The single `ai_settings` row (Direccion-only read/update). */
export type AiSettings = {
  id: boolean;
  monthly_cap_usd: number;
  model: string;
  input_price_usd_per_mtok: number;
  output_price_usd_per_mtok: number;
  max_output_tokens: number;
  updated_at: string;
  updated_by?: Identifier | null;
};

/** Fields Direccion may change on `ai_settings`. */
export type AiSettingsUpdate = Partial<
  Pick<
    AiSettings,
    | "monthly_cap_usd"
    | "model"
    | "input_price_usd_per_mtok"
    | "output_price_usd_per_mtok"
    | "max_output_tokens"
  >
>;

/** One row per AI call attempt; no prompt content is ever stored. */
export type AiUsageLogEntry = {
  id: Identifier;
  created_at: string;
  sales_id: Identifier;
  deal_id?: Identifier | null;
  action: AiAction;
  model: string;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
  status: "ok" | "rejected_cap" | "error";
};

/** One row of the `ai_usage_monthly` view (month = first day, `YYYY-MM-DD`). */
export type AiMonthlyUsage = {
  month: string;
  action: AiAction;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
};

/** Current-month spend against the monthly cap (USD). */
export type AiSpendStatus = {
  month: string;
  spentUsd: number;
  capUsd: number;
  remainingUsd: number;
};

/** The maintained short summary of a lead (1:1). */
export type DealAiSummary = {
  id: Identifier;
  deal_id: Identifier;
  summary: string;
  updated_at: string;
  source_note_id?: Identifier | null;
};

/** Buyer-profile chips proposed for a note (ids of the existing catalogues). */
export type AiNoteChips = {
  buyer_type_id: Identifier | null;
  credit_type_id: Identifier | null;
  timeline_id: Identifier | null;
  motivator_ids: Identifier[];
  objection_ids: Identifier[];
};

export type AiNoteSuggestionsResult = {
  chips: AiNoteChips;
  missing_items: string[];
  summary: string;
};
export type AiSuggestMessageResult = { message: string; because: string };
export type AiWhatsMissingResult = {
  missing_items: string[];
  next_action: string;
};

type DealAiSuggestionBase = {
  id: Identifier;
  deal_id: Identifier;
  status: AiSuggestionStatus;
  created_by?: Identifier | null;
  source_note_id?: Identifier | null;
  created_at: string;
  decided_at?: string | null;
};

/** A stored proposal; `payload` is discriminated by `kind`. */
export type DealAiSuggestion = DealAiSuggestionBase &
  (
    | { kind: "note_chips"; payload: AiNoteSuggestionsResult }
    | { kind: "missing_items"; payload: AiWhatsMissingResult }
    | { kind: "message"; payload: AiSuggestMessageResult }
  );

export type AiCopilotRequest = {
  action: AiAction;
  dealId: Identifier;
  /** Required for `note_suggestions`. */
  noteId?: Identifier;
  /** Required for `suggest_message`. */
  objective?: string;
};

/** Successful copilot response; `result` is discriminated by `action`. */
export type AiCopilotResponse = { suggestionId: Identifier } & (
  | { action: "note_suggestions"; result: AiNoteSuggestionsResult }
  | { action: "suggest_message"; result: AiSuggestMessageResult }
  | { action: "whats_missing"; result: AiWhatsMissingResult }
);

export const AI_ERROR_CODES = [
  "monthly_cap_reached",
  "ai_unavailable",
  "invalid_response",
] as const;
export type AiErrorCode = (typeof AI_ERROR_CODES)[number];

export type AiDecision = {
  status: Exclude<AiSuggestionStatus, "pending">;
  /** Replaces the message text; only valid with status `edited` on a message. */
  editedText?: string;
};

/**
 * Almikia Epic EL (TASK-007): lead assignment. A channel is a row in the extensible
 * `lead_assignment_channels` catalogue (no CHECK on `code`, a new channel is one row).
 */
export type LeadAssignmentChannel = {
  code: string;
  label: string;
  is_active: boolean;
} & Pick<RaRecord, "id">;

/** One row per project: the rotation switch and its fallback policy (`lead_assignment_rules`). */
export type LeadAssignmentRule = {
  project_id: Identifier;
  is_enabled: boolean;
  fallback_to_eligible: boolean;
  /** Epic EL2: cap of open unattended leads per advisor; null = rule off. */
  max_open_unattended_leads?: number | null;
  /** Epic EL2: hours without contact in Contacto inicial / Discovery before an advisor is skipped; null = rule off. */
  overdue_threshold_hours?: number | null;
  updated_by?: Identifier | null;
  updated_at: string;
} & Pick<RaRecord, "id">;

/**
 * Whether an advisor takes part in a project's rotation (`lead_assignment_eligible_advisors`,
 * composite PK project_id + sales_id; `id` is the synthetic identifier FakeRest requires).
 */
export type LeadAssignmentEligibleAdvisor = {
  project_id: Identifier;
  sales_id: Identifier;
  is_eligible: boolean;
} & Pick<RaRecord, "id">;

/**
 * A weekly window an advisor chooses to receive leads (ISO weekday, 1 = Monday .. 7 = Sunday,
 * times as "HH:MM" or "HH:MM:SS" in America/Mexico_City). It ends via `ended_at`, never DELETE.
 */
export type AdvisorAvailabilityBlock = {
  sales_id: Identifier;
  weekday: number;
  start_time: string;
  end_time: string;
  created_at: string;
  ended_at?: string | null;
} & Pick<RaRecord, "id">;

export type LeadAssignmentMethod = "rotation" | "fallback_rotation" | "manual";
export type LeadAssignmentOutcome = "assigned" | "no_candidates";

/** One append-only entry of the assignment log (`lead_assignments`). */
export type LeadAssignment = {
  deal_id: Identifier;
  project_id?: Identifier | null;
  /** Null for outcome `no_candidates`. */
  sales_id?: Identifier | null;
  channel_code: string;
  method: LeadAssignmentMethod;
  outcome: LeadAssignmentOutcome;
  assigned_by?: Identifier | null;
  assigned_at: string;
  /** Epic EL2: why the lead went to this advisor; null/absent for rows logged before EL2. */
  reason?: LeadAssignmentReason | null;
  /** Epic EL3: the owner before this assignment (null on a first assignment). */
  previous_sales_id?: Identifier | null;
  /** Epic EL3: the written reason of a documented reassignment (Direccion, Team Leader). */
  reason_text?: string | null;
} & Pick<RaRecord, "id">;

/**
 * Result of `CrmDataProvider.assignLead()`. Deliberately carries no ranking, count or score of
 * advisors: only who got the lead (null when nobody qualified) and how.
 */
export type AssignLeadResult = {
  outcome: LeadAssignmentOutcome;
  sales_id: Identifier | null;
  method: Exclude<LeadAssignmentMethod, "manual"> | null;
  /** Epic EL2: set for BPO leads (carousel); absent for corretaje. */
  reason?: LeadAssignmentReason | null;
};

/** Direccion's per-project switches written by the "Asignacion de leads" screen. */
export type LeadAssignmentRuleInput = {
  project_id: Identifier;
  is_enabled: boolean;
  fallback_to_eligible: boolean;
  /** Epic EL2 skip-rule parameters; omitted = unchanged on update, null = rule off. */
  max_open_unattended_leads?: number | null;
  overdue_threshold_hours?: number | null;
};

/** A channel row written by the "Asignacion de leads" screen (add, or toggle `is_active`). */
export type LeadAssignmentChannelInput = {
  code: string;
  label: string;
  is_active: boolean;
};

/**
 * Quoter (Almikia Epic EQ, TASK-015/016). Column names are identical to
 * `public.quote_plan_templates`, `public.quotes` and `public.unit_reservations`.
 * `plan_type` is a label only: the schedule is driven by the percentages/counts.
 */
export const QUOTE_PLAN_TYPES = [
  "enganche_mensualidades",
  "diferido",
  "contra_entrega",
  "credito",
] as const;
export type QuotePlanType = (typeof QUOTE_PLAN_TYPES)[number];

export type QuoteStatus = "activa" | "cancelada" | "convertida";
export type UnitReservationStatus = "activa" | "liberada";

/** The five percentages and two counts that drive a payment schedule. */
export type QuotePlanParameters = {
  down_payment_pct: number;
  down_payment_count: number;
  monthly_payments_pct: number;
  monthly_payments_count: number;
  deferred_payments_pct: number;
  deferred_payments_count: number;
  delivery_pct: number;
  credit_pct: number;
  discount_pct?: number | null;
};

export type QuotePlanTemplate = {
  project_id: Identifier;
  name: string;
  plan_type: QuotePlanType;
  archived_at?: string | null;
  created_by?: Identifier | null;
  created_at: string;
  updated_at: string;
} & QuotePlanParameters &
  Pick<RaRecord, "id">;

/** The plan frozen on a quote (`quotes.plan_snapshot`, built by `create_quote()`). */
export type QuotePlanSnapshot = QuotePlanParameters & {
  template_name: string;
  plan_type: QuotePlanType;
};

export type Quote = {
  deal_id: Identifier;
  unit_id: Identifier;
  project_id: Identifier;
  /** Null on an EQ2 quote: its plan lives in `quote_details`. */
  plan_template_id: Identifier | null;
  plan_snapshot: QuotePlanSnapshot;
  unit_price: number;
  client_first_name: string;
  status: QuoteStatus;
  cancelled_at?: string | null;
  cancelled_by?: Identifier | null;
  share_token?: string | null;
  token_expires_at?: string | null;
  token_revoked_at?: string | null;
  created_by: Identifier;
  created_at: string;
  /**
   * Almikia batch EQ3 (TASK-114): `pendiente` (custom plan awaiting the approver),
   * `aprobada` (every other quote and an approved custom plan; a row without the
   * field counts as approved) or `rechazada` (with `approval_comment`).
   */
  approval_status?: QuoteApprovalStatus;
  approval_comment?: string | null;
  approval_decided_by?: Identifier | null;
  approval_decided_at?: string | null;
} & Pick<RaRecord, "id">;

export type QuoteApprovalStatus = "pendiente" | "aprobada" | "rechazada";

/** `quoter_plan_approvers`: who approves custom plans (configuration, never seeded). */
export type QuoterPlanApprover = {
  sales_id: Identifier;
  active: boolean;
  updated_by?: Identifier | null;
  updated_at: string;
} & Pick<RaRecord, "id">;

export type QuoterPlanNoticeKind =
  | "approval_requested"
  | "approved"
  | "rejected";

/** `quoter_plan_notices`: CRM notice of the approval flow ("Planes por aprobar" + outcome). */
export type QuoterPlanNotice = {
  recipient_sales_id: Identifier;
  kind: QuoterPlanNoticeKind;
  quote_id: Identifier;
  comment?: string | null;
  read_at?: string | null;
  created_at: string;
} & Pick<RaRecord, "id">;

export type UnitReservation = {
  unit_id: Identifier;
  deal_id: Identifier;
  quote_id?: Identifier | null;
  reserved_by: Identifier;
  status: UnitReservationStatus;
  reserved_at: string;
  released_at?: string | null;
  released_by?: Identifier | null;
  release_reason?: string | null;
  created_at: string;
} & Pick<RaRecord, "id">;

/** `createQuote` input; `clientFirstName` defaults to the lead's first contact. */
export type CreateQuoteInput = {
  dealId: Identifier;
  unitId: Identifier;
  planTemplateId: Identifier;
  clientFirstName?: string;
};

/** `reserveUnit` input; `quoteId` optionally links the reservation to a quote. */
export type ReserveUnitInput = {
  unitId: Identifier;
  dealId: Identifier;
  quoteId?: Identifier;
};

/**
 * The minimal payload `get_public_quote()` returns to an anonymous visitor:
 * no ids, no advisor, no lead or contact data.
 */
export type PublicQuote = {
  client_first_name: string;
  project_name: string;
  unit_identifier: string;
  unit_type: UnitType;
  unit_price: number;
  plan: QuotePlanSnapshot;
  issued_at: string;
  valid_until: string;
  /** Epic EQ2 (TASK-101): the two-sheet content; null for a legacy (EQ) quote. */
  detail?: PublicQuoteDetail | null;
};

/** One model of the public comparison table, priced at the quote's list. */
export type PublicQuoteModel = {
  code: string;
  name: string;
  total_construction: number | null;
  program: string | null;
  price_from: number | null;
};

/**
 * The minimum an anonymous visitor needs to render the two sheets of an EQ2
 * quote (`get_public_quote().detail`): the unit, the price breakdown, the
 * frozen calendar, the model comparison, the scarcity and the advisor card
 * (name and WhatsApp only). No lead, contact, other quote or internal id.
 */
export type PublicQuoteDetail = {
  /** The quote number shown as the folio. */
  folio: Identifier;
  list_number: number;
  scheme_name: string;
  is_custom: boolean;
  /** ISO dates `YYYY-MM-DD`. */
  issued_on: string;
  valid_until: string;
  unit: {
    model_code: string | null;
    model_name: string | null;
    stage: number | null;
    block_label: string | null;
    street: string | null;
    surface_m2: number | null;
    measures: string | null;
    orientation: string | null;
    lot_type: string | null;
    lot_type_description: string | null;
    roof_included: boolean;
    roof_chosen: boolean;
    m2_per_floor: number | null;
    total_construction: number | null;
    program: string | null;
    garage: string | null;
  };
  prices: {
    base: number;
    excess_m2: number;
    excess: number;
    roof: number;
    extras: number;
    gross: number;
    discount_pct: number;
    discount: number;
    final: number;
    apartado: number;
  };
  plan: {
    down_pct: number;
    monthly_pct: number;
    deed_pct: number;
    months: number;
  };
  calendar: QuoterCalendarRow[];
  models: PublicQuoteModel[];
  scarcity: {
    stage: number | null;
    in_stage: number | null;
    in_development: number;
  };
  /** `whatsapp` is E.164 (`+` and digits), null when the advisor has none. */
  advisor: { name: string; whatsapp: string | null };
};

/**
 * Definitive quoter (Almikia Epic EQ2, TASK-097/098). Column names are
 * identical to `public.quoter_configs`, `quoter_models`, `quoter_price_lists`,
 * `quoter_model_list_prices`, `quoter_schemes`, `quoter_unit_details` and
 * `quote_details`; the pure engine (`quoter/quoterEngine.ts`) mirrors the SQL.
 */
export type QuoterConfig = {
  project_id: Identifier;
  apartado_amount: number;
  apartado_days: number;
  quote_validity_days: number;
  term_months: number;
  apartados_count_toward_list: boolean;
  excess_price_per_m2: number;
  released_stages: number[];
  client_protection_days: number;
  created_at: string;
  updated_at: string;
} & Pick<RaRecord, "id">;

export type QuoterModel = {
  project_id: Identifier;
  code: string;
  name: string;
  base_price: number;
  roof_price: number;
  m2_per_floor?: number | null;
  total_construction?: number | null;
  program?: string | null;
  garage?: string | null;
  sort_order: number;
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
} & Pick<RaRecord, "id">;

/** `increment_pct` is the % over the PREVIOUS list (null on list 1). */
export type QuoterPriceList = {
  project_id: Identifier;
  list_number: number;
  block_size: number;
  increment_pct?: number | null;
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
} & Pick<RaRecord, "id">;

/** A directly captured price of a model on a list; wins over the increment. */
export type QuoterModelListPrice = {
  price_list_id: Identifier;
  model_id: Identifier;
  price: number;
  created_at: string;
  updated_at: string;
} & Pick<RaRecord, "id">;

export type QuoterScheme = {
  project_id: Identifier;
  name: string;
  down_pct: number;
  monthly_pct: number;
  deed_pct: number;
  discount_pct: number;
  is_custom_allowed: boolean;
  sort_order: number;
  archived_at?: string | null;
  created_at: string;
  updated_at: string;
} & Pick<RaRecord, "id">;

export type QuoterUnitExtra = { label: string; price: number };

export type QuoterUnitDetail = {
  unit_id: Identifier;
  model_id?: Identifier | null;
  stage?: number | null;
  block_label?: string | null;
  street?: string | null;
  surface_m2?: number | null;
  excess_m2: number;
  measures?: string | null;
  orientation?: string | null;
  lot_type?: string | null;
  lot_type_description?: string | null;
  roof_included: boolean;
  unit_note?: string | null;
  extras: QuoterUnitExtra[];
  is_blocked: boolean;
  /** "Por verificar": quoting is allowed but warns until Direccion confirms. */
  needs_verification: boolean;
  verified_at?: string | null;
  verified_by?: Identifier | null;
  created_at: string;
  updated_at: string;
} & Pick<RaRecord, "id">;

/** One row of a payment calendar (`seq` 0 = apartado, 1 = down payment, ..., last = deed). */
export type QuoterCalendarRow = {
  seq: number;
  /** ISO date `YYYY-MM-DD`. */
  due_date: string;
  concept: string;
  amount: number;
};

/** The resolved plan of a quote: a scheme of the project or a custom plan. */
export type QuoterPlan = {
  scheme_id: Identifier | null;
  scheme_name: string;
  is_custom: boolean;
  down_pct: number;
  monthly_pct: number;
  deed_pct: number;
  discount_pct: number;
  /** Own months of a custom plan; null/0 means the project term applies. */
  custom_months: number | null;
};

/** The custom ("a la medida") plan typed by direccion / lead_manager. */
export type QuoterCustomPlanInput = {
  down_pct: number;
  monthly_pct: number;
  deed_pct: number;
  months?: number | null;
  discount_pct: number;
};

/** Amounts and calendar of the plan (`quoter_build_plan()`). */
export type QuoterPlanResult = {
  final: number;
  discount_amount: number;
  apartado: number;
  down_payment: number;
  installments: number;
  installment_amount: number;
  deed_amount: number;
  deed_month: number;
  plazo: number;
  issued_on: string;
  valid_until: string;
  calendar: QuoterCalendarRow[];
};

/** Prices of a unit at a list plus its plan (`quoter_calc()`). */
export type QuoterCalcResult = QuoterPlanResult & {
  list: number;
  base: number;
  excess: number;
  roof: number;
  extras: number;
  gross: number;
};

/** What `quoter_preview()` returns: the calc plus unit context and scarcity. */
export type QuoterPreview = QuoterCalcResult & {
  unit_id: Identifier;
  unit_identifier: string;
  project_id: Identifier;
  model_id: Identifier;
  plan: QuoterPlan;
  roof_chosen: boolean;
  roof_included: boolean;
  excess_m2: number;
  unit_unverified: boolean;
  protection_days: number;
  scarcity: {
    stage: number | null;
    in_stage: number | null;
    in_development: number;
  };
};

/** The frozen result stored 1:1 with a quote (`public.quote_details`). */
export type QuoteDetail = {
  quote_id: Identifier;
  list_number: number;
  scheme_id?: Identifier | null;
  scheme_name: string;
  is_custom: boolean;
  plan_down_pct: number;
  plan_monthly_pct: number;
  plan_deed_pct: number;
  plan_discount_pct: number;
  plan_months: number;
  custom_months?: number | null;
  roof_chosen: boolean;
  roof_included: boolean;
  excess_m2: number;
  base_amount: number;
  excess_amount: number;
  roof_amount: number;
  extras_amount: number;
  gross_amount: number;
  discount_amount: number;
  final_amount: number;
  apartado_amount: number;
  issued_on: string;
  valid_until: string;
  protected_until?: string | null;
  calc_snapshot: QuoterPreview;
  reissued_from_quote_id?: Identifier | null;
  unit_unverified: boolean;
  created_at: string;
} & Pick<RaRecord, "id">;

/** The per-project configuration tables only direccion writes (RLS). */
export const QUOTER_CONFIG_RESOURCES = [
  "quoter_configs",
  "quoter_models",
  "quoter_price_lists",
  "quoter_model_list_prices",
  "quoter_schemes",
  "quoter_unit_details",
] as const;
export type QuoterConfigResource = (typeof QUOTER_CONFIG_RESOURCES)[number];

/** `quoterPreview` input. */
export type QuoterPreviewInput = {
  unitId: Identifier;
  schemeId?: Identifier | null;
  roof?: boolean;
  custom?: QuoterCustomPlanInput | null;
};

/** `quoterCreateQuote` input; `clientFirstName` defaults to the lead's first contact. */
export type QuoterCreateQuoteInput = QuoterPreviewInput & {
  dealId: Identifier;
  clientFirstName?: string;
};

export type QuoterReissueResult = {
  new_quote_id: Identifier;
  previous_quote_id: Identifier;
  previous_final: number;
  new_final: number;
  difference: number;
  direction: "up" | "down" | "same";
  list: number;
};

export type QuoterTermImpact = {
  current_term: number;
  new_term: number;
  valid_quotes: number;
  changed_quotes: number;
  installment_total_before: number;
  installment_total_after: number;
  installment_delta: number;
};

export type QuoterModelComparison = {
  model_id: Identifier;
  code: string;
  name: string;
  m2_per_floor: number | null;
  total_construction: number | null;
  program: string | null;
  garage: string | null;
  price_from: number | null;
};

export type QuoterPanel = {
  project_id: Identifier;
  current_list: number | null;
  counted_sales: number;
  apartados_count_toward_list: boolean;
  next_list: number | null;
  sales_missing_for_next_list: number | null;
  available_units: number;
  scarcity: { stage: number | null; available: number; released: boolean }[];
  models: QuoterModelComparison[];
};

/**
 * Almikia Epic E4 (TASK-024/025/026): operations. Column names and value sets
 * are identical to `public.operations` / `public.operation_advisor_history`
 * (`supabase/schemas/01_tables.sql`); keep both in lockstep.
 */
export const OPERATION_COMMERCIAL_STATUSES = [
  "apartada",
  "cerrada",
  "cancelada",
] as const;
export type OperationCommercialStatus =
  (typeof OPERATION_COMMERCIAL_STATUSES)[number];

export const OPERATION_ADMINISTRATIVE_STATUSES = [
  "pendiente",
  "en_revision",
  "observado",
  "validado",
] as const;
export type OperationAdministrativeStatus =
  (typeof OPERATION_ADMINISTRATIVE_STATUSES)[number];

export const OPERATION_SEMAPHORES = [
  "verde",
  "amarillo",
  "naranja",
  "rojo",
] as const;
export type OperationSemaphore = (typeof OPERATION_SEMAPHORES)[number];

export const OPERATION_TYPES = [
  "propia",
  "inter_bpo",
  "intercorretaje",
  "codigo_azul",
] as const;
export type OperationType = (typeof OPERATION_TYPES)[number];

/** One sale. The id is the formatted text `ALM-{PROJ}-{YY}-{NNNNN}`, assigned by the backend. */
export type Operation = {
  id: string;
  deal_id: Identifier;
  /**
   * These three are NULL in the database for a corretaje operation (it has `option_id`
   * instead). They stay required here because the current BPO-only consumers (FakeRest
   * operations provider, screens) rely on them; the corretaje UI ticket should split the type.
   */
  reservation_id: Identifier;
  unit_id: Identifier;
  project_id: Identifier;
  /** Epic EC: set for a corretaje operation (id `ALM-COR-{YY}-{NNNNN}`), never changes. */
  option_id?: Identifier | null;
  client_id?: Identifier | null;
  sales_id: Identifier;
  lead_type?: LeadType | null;
  operation_type: OperationType;
  commercial_status: OperationCommercialStatus;
  administrative_status: OperationAdministrativeStatus;
  semaphore: OperationSemaphore;
  semaphore_reason?: string | null;
  sale_value?: number | null;
  /** `{PROJ}-{UNIT}-{YYMMDD}`, set once on signing and immutable afterwards. */
  contract_folio?: string | null;
  signed_on?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: Identifier | null;
  cancel_reason?: string | null;
  created_by?: Identifier | null;
  created_at: string;
  updated_at: string;
  updated_by?: Identifier | null;
};

/** A row of the `operations_summary` view: the operation plus display names. */
export type OperationSummary = Operation & {
  project_code: string | null;
  project_name: string | null;
  unit_identifier: string | null;
  sales_name: string | null;
  client_name: string | null;
  /** Epic EC: corretaje columns (null for a BPO operation). */
  option_address?: string | null;
  vobo_status?: CorretajeVoboStatus | null;
  /** The split case frozen at VoBo approval; null before. */
  split_case_code?: CorretajeCaseCode | null;
};

/** Almikia Epic EC (TASK-031): value sets of `public.operation_participants` / `corretaje_sale_administration`. */
export const OPERATION_PARTICIPANT_ROLES = [
  "vendedora",
  "opcionadora",
] as const;
export type OperationParticipantRole =
  (typeof OPERATION_PARTICIPANT_ROLES)[number];

export const CORRETAJE_COLEGA_FOUND_BY = [
  "asesora_proactiva",
  "lead_manager",
  "direccion_corretaje",
] as const;
export type CorretajeColegaFoundBy = (typeof CORRETAJE_COLEGA_FOUND_BY)[number];

export const CORRETAJE_PROPERTY_OWNERS = ["almikia", "colega"] as const;
export type CorretajePropertyOwner = (typeof CORRETAJE_PROPERTY_OWNERS)[number];

export const CORRETAJE_VOBO_STATUSES = [
  "pendiente",
  "solicitado",
  "aprobado",
  "observado",
] as const;
export type CorretajeVoboStatus = (typeof CORRETAJE_VOBO_STATUSES)[number];

/** Decisions accepted by `set_corretaje_vobo` (direccion only). */
export const CORRETAJE_VOBO_DECISIONS = [
  "aprobado",
  "observado",
  "reopen",
] as const;
export type CorretajeVoboDecision = (typeof CORRETAJE_VOBO_DECISIONS)[number];

/** A vendedora / opcionadora of a corretaje operation; AMK is a flag with no `sales_id`. */
export type OperationParticipant = {
  id: Identifier;
  operation_id: string;
  role: OperationParticipantRole;
  sales_id?: Identifier | null;
  is_amk: boolean;
  created_by?: Identifier | null;
  created_at: string;
  /** Set when a superseding set replaces the row; rows are never deleted. */
  ended_at?: string | null;
};

/** The 1:1 sale administration of a corretaje operation (`public.corretaje_sale_administration`). */
export type CorretajeSaleAdministration = {
  id: Identifier;
  operation_id: string;
  shared_with_colega: boolean;
  colega_id?: Identifier | null;
  colega_found_by?: CorretajeColegaFoundBy | null;
  property_owner: CorretajePropertyOwner;
  /** Total commission C. */
  commission_total?: number | null;
  vobo_status: CorretajeVoboStatus;
  vobo_requested_by?: Identifier | null;
  vobo_requested_at?: string | null;
  vobo_decided_by?: Identifier | null;
  vobo_decided_at?: string | null;
  vobo_note?: string | null;
  snapshot_case_code?: CorretajeCaseCode | null;
  snapshot_scheme_id?: Identifier | null;
  snapshot_colega_amount?: number | null;
  snapshot_vendedora_amount?: number | null;
  snapshot_opcionadora_amount?: number | null;
  snapshot_almikia_amount?: number | null;
  snapshot_taken_at?: string | null;
  created_by?: Identifier | null;
  created_at: string;
  updated_at: string;
  updated_by?: Identifier | null;
};

/** Result of `get_corretaje_operation_split`; `is_snapshot` is true once the VoBo is approved. */
export type CorretajeOperationSplit = CorretajeSplitResult & {
  is_snapshot: boolean;
};

/** Append-only advisor history of an operation (`from_sales_id` is null on the first row). */
export type OperationAdvisorHistory = {
  id: Identifier;
  operation_id: string;
  from_sales_id?: Identifier | null;
  to_sales_id: Identifier;
  changed_by?: Identifier | null;
  changed_at: string;
  reason?: string | null;
};

/** Almikia Epic E5 (TASK-033/034): value sets of `public.documents` / `public.document_versions`. */
export const DOCUMENT_TYPES = [
  "identificacion",
  "comprobante_domicilio",
  "constancia_fiscal",
  "contrato",
  "apartado",
  "pld",
  "otro",
  // Almikia Epic E6: the advisor's own invoice, the requirement of her payable.
  "factura_asesora",
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const DOCUMENT_RESULTS = [
  "pendiente",
  "validado",
  "observado",
  "rechazado",
] as const;
export type DocumentResult = (typeof DOCUMENT_RESULTS)[number];

/** What a validator may decide on a version (`pendiente` is the initial state only). */
export type DocumentValidationResult = Exclude<DocumentResult, "pendiente">;

/** The logical file of an operation (one identification, one contract...). Never hard-deleted. */
export type Document = {
  id: Identifier;
  operation_id: string;
  doc_type: DocumentType;
  title?: string | null;
  created_by: Identifier;
  created_at: string;
  retention_until: string;
  deleted_at?: string | null;
  deleted_by?: Identifier | null;
  delete_reason?: string | null;
};

/** One upload of a document: append-only; only review, validation and soft delete change once. */
export type DocumentVersion = {
  id: Identifier;
  document_id: Identifier;
  version_number: number;
  storage_path: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  /** Lowercase hex SHA-256 of the file, computed client-side before registering. */
  sha256: string;
  uploaded_by: Identifier;
  uploaded_at: string;
  reviewed_by?: Identifier | null;
  reviewed_at?: string | null;
  review_note?: string | null;
  validated_by?: Identifier | null;
  validated_at?: string | null;
  result: DocumentResult;
  result_reason?: string | null;
  /** Snapshot of the operation's advisor when the version was validated. */
  advisor_at_validation?: Identifier | null;
  retention_until: string;
  deleted_at?: string | null;
  deleted_by?: Identifier | null;
  delete_reason?: string | null;
};

/** A document with its versions, oldest first (what an operation's documents tab lists). */
export type DocumentWithVersions = Document & { versions: DocumentVersion[] };

/** A row of the `operation_documents_summary` view: a document and its latest non-deleted version. */
export type OperationDocumentSummary = {
  document_id: Identifier;
  operation_id: string;
  doc_type: DocumentType;
  title?: string | null;
  created_by: Identifier;
  created_at: string;
  deleted_at?: string | null;
  latest_version_id?: Identifier | null;
  latest_version_number?: number | null;
  latest_result?: DocumentResult | null;
  latest_validated_by?: Identifier | null;
  latest_validated_at?: string | null;
  latest_uploaded_at?: string | null;
  version_count: number;
  pending_count: number;
};

/** A row of the append-only `public.audit_log`. Free text and personal data are never copied in. */
export type AuditLogEntry = {
  id: Identifier;
  occurred_at: string;
  actor_user_id?: string | null;
  actor_sales_id?: Identifier | null;
  actor_role?: AppRole | null;
  table_name: string;
  row_id: string;
  operation_id?: string | null;
  action: "insert" | "update" | "delete";
  old_data?: Record<string, unknown> | null;
  new_data?: Record<string, unknown> | null;
  changed_columns?: string[] | null;
  reason?: string | null;
};

/** Input of `uploadOperationDocument`: a new document (no `documentId`) or a new version of one. */
export type UploadOperationDocumentInput = {
  operationId: string;
  file: File;
  docType: DocumentType;
  title?: string;
  /** Appends a version to this document instead of creating one. */
  documentId?: Identifier;
};

// Almikia Epic E6 (TASK-039): invoicing row types live in their own module.
export * from "./invoicing/invoicingTypes";

// Almikia Epic EG (TASK-043): expense row types live in their own module.
export * from "./expenses/expenseTypes";

// Almikia Epic PL (TASK-061): pilot feedback report types live in their own module.
export * from "./feedback/feedbackTypes";

// Almikia Epic EL2 (TASK-073): BPO carousel resource types live in their own module.
export * from "./carouselTypes";

// Almikia Epic EW2 (TASK-079): WhatsApp conversation resource types live in their own module.
export * from "./whatsappTypes";

// Almikia Epic EN (TASK-091): internal WhatsApp notices UI types live in their own module.
export * from "./internalNoticesTypes";

// Almikia Epic EC2b (TASK-124): contact expediente types live in their own module.
export * from "./contactExpedienteTypes";
